import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config';
import { Project } from '../types/project';
import { ClothingAvailability } from '../types/clothingAvailability';
import { addAuthHeaders } from '../../api';
import { Alias } from '../types/alias';
import { Staff } from '../types/staff';
import { Order } from '../types/order';
import { NewOrder } from '../types/api/newOrder';
import { UpdateOrder } from '../types/api/updateOrder';
import { SelectItem } from '../types/util/selectItem';
import { Region } from '../types/region';
import { Product } from '../types/product';
import { Category } from '../types/category';
import { PickupInfo } from '../types/pickupInfo';

// Define a service using a base URL and expected endpoints
export const webshopApi = createApi({
  reducerPath: 'webshop',
  baseQuery: fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'projects',
    'clothing',
    'workers',
    'alias',
    'orders',
    'machines',
    'regions',
    'equipment',
    'clothingOrders',
  ],
  endpoints: (builder) => ({
    getProject: builder.query<Project, number>({
      query: (id) => `/projects/${id}`,
      providesTags: ['projects'],
    }),
    getAllProjects: builder.query<Project[], void | { q?: string }>({
      query: (q) => ({
        url: '/projects',
        params: {
          q,
        },
      }),
      providesTags: ['projects'],
    }),
    getProjectOptions: builder.query<SelectItem<number>[], void>({
      query: () => '/projects/options',
      providesTags: ['projects'],
    }),
    getClothing: builder.query<ClothingAvailability, void|number|string>({
      query: (id) => (id ? `/clothing/${id}` : '/clothing'),
      providesTags: ['clothing'],
    }),
    getAllWorkers: builder.query<Staff[], void>({
      query: () => '/workers',
      providesTags: ['workers'],
    }),
    getUser: builder.query<Staff, void>({
      query: () => '/workers/me',
      providesTags: ['workers'],
    }),
    getMachineOptions: builder.query<SelectItem<string>[], void>({
      query: () => '/machines/options',
      providesTags: ['machines'],
    }),
    getRegions: builder.query<Region[], void>({
      query: () => '/regions',
      providesTags: ['regions'],
    }),
    getAlias: builder.query<Alias, string | number>({
      query: (workerId) => ({
        url: `/alias/${workerId}`,
      }),
      providesTags: ['alias'],
    }),
    putAlias: builder.mutation<void, { id: number | string, target: string }>({
      query: ({ id, target }) => ({
        url: `/alias/${id}`,
        method: 'PUT',
        body: `"${target}"`,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['alias'],
    }),
    deleteAlias: builder.mutation<void, number | string>({
      query: (workerId) => ({
        url: `/alias/${workerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['alias'],
    }),
    postOrder: builder.mutation<Order, NewOrder>({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['orders', 'clothing'],
    }),
    putOrder: builder.mutation<Order, { orderId: string, body: UpdateOrder }>({
      query: ({ orderId, body }) => ({
        url: `/orders/${orderId}`,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['orders', 'clothing'],
    }),
    getOrder: builder.query<Order, string>({
      query: (orderId) => (`/orders/${orderId}`),
      providesTags: ['orders'],
    }),
    getClothingOrders: builder.query<Order[], { start: string, end: string }>({
      query: ({ start, end }) => (`/orders/clothing/?from=${start}&to=${end}`),
      providesTags: ['clothingOrders'],
    }),
    getUserOrders: builder.query<Order[], string | void>({
      query: (date) => (date ? `/me/orders?from=${date}` : '/me/orders'),
      providesTags: ['orders'],
    }),
    getApprovalOrders: builder.query<Order[], void>({
      query: () => '/orders/approval',
      providesTags: ['orders'],
    }),
    getEquipmentCategories: builder.query<Category[], void>({
      query: () => '/equipment/categories',
    }),
    getProductsInCategory: builder.query<Product[], string>({
      query: (category) => `/equipment/categories/${category}`,
    }),
    searchProducts: builder.query<Product[], string>({
      query: (search) => ({
        url: '/equipment',
        params: { search },
      }),
    }),
    getPickupInfo: builder.query<PickupInfo[], void>({
      query: () => '/orders/pickup-info',
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetProjectQuery,
  useGetAllProjectsQuery,
  useGetProjectOptionsQuery,
  useGetClothingQuery,
  useGetAllWorkersQuery,
  useGetUserQuery,
  useGetMachineOptionsQuery,
  useGetRegionsQuery,
  useGetAliasQuery,
  useGetOrderQuery,
  useGetUserOrdersQuery,
  useGetApprovalOrdersQuery,
  usePutOrderMutation,
  usePutAliasMutation,
  useDeleteAliasMutation,
  usePostOrderMutation,
  useGetEquipmentCategoriesQuery,
  useGetProductsInCategoryQuery,
  useSearchProductsQuery,
  useGetClothingOrdersQuery,
  useGetPickupInfoQuery,
  util,
} = webshopApi;

export default webshopApi.reducer;
